import React, { useContext, useEffect, useMemo, useState } from "react";
import { fleetErrorMessage } from "../utils/fleetFeedback";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import {
  API_Fleet_ID_parking_areas,
  API_Fleet_ID_parking_booking,
  API_Fleet_ID_parking_bookings,
  API_Fleet_ID_parking_config,
  API_REQUEST_HEADERS_BASEURL,
} from "../utils/Constants";

const { Text, Title } = Typography;

function replaceFleetId(endpoint, fleetId) {
  return endpoint.replace("{fleetId}", encodeURIComponent(fleetId));
}

function toIsoString(value) {
  if (!value) return "";
  if (typeof value.format === "function") return value.format("YYYY-MM-DDTHH:mm:ssZ");
  if (typeof value.toDate === "function") {
    const parsed = value.toDate();
    return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().replace(/\.\d{3}Z$/, "+00:00");
  }
  if (typeof value.toISOString === "function") return value.toISOString().replace(/\.\d{3}Z$/, "+00:00");
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().replace(/\.\d{3}Z$/, "+00:00");
}

function formatDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function getBookingTime(record) {
  const candidates = [record?.startDate, record?.endDate];
  for (const value of candidates) {
    const parsed = value ? new Date(value).getTime() : NaN;
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
}

function sortBookingsNewestFirst(items) {
  return [...items].sort((a, b) => getBookingTime(b) - getBookingTime(a));
}

function pickResponseData(response) {
  return response?.data?.data ?? response?.data ?? null;
}

export default function Parking() {
  const { t } = useTranslation();
  const { getFleetID } = useContext(AuthContext);
  const fleetId = getFleetID();

  const [config, setConfig] = useState(null);
  const [providerVersion, setProviderVersion] = useState("v2");
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [searchingAreas, setSearchingAreas] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [parkingAreas, setParkingAreas] = useState([]);
  const [configForm] = Form.useForm();
  const [bookingForm] = Form.useForm();
  const [areaForm] = Form.useForm();

  const configEndpoint = useMemo(
    () => (fleetId ? replaceFleetId(API_Fleet_ID_parking_config, fleetId) : ""),
    [fleetId]
  );
  const bookingsEndpoint = useMemo(
    () => (fleetId ? replaceFleetId(API_Fleet_ID_parking_bookings, fleetId) : ""),
    [fleetId]
  );
  const areasEndpoint = useMemo(
    () => (fleetId ? replaceFleetId(API_Fleet_ID_parking_areas, fleetId) : ""),
    [fleetId]
  );

  const loadConfig = async () => {
    if (!fleetId) return;
    setLoadingConfig(true);
    try {
      const response = await API_REQUEST_HEADERS_BASEURL.get(configEndpoint);
      const data = pickResponseData(response) || {};
      setConfig(data);
      setProviderVersion(data.providerVersion || "v2");
      configForm.setFieldsValue({
        providerVersion: data.providerVersion || "v2",
        v2AuthKey: "",
        v3AuthUrl: data?.v3?.authUrl || "https://aaa.boschindia-mobilitysolutions.de",
        v3Realm: data?.v3?.realm || "Rlm_ROBOIN_BLOS_EU",
        v3BaseUrl: data?.v3?.baseUrl || "https://www.de.l-os.com/api/services/road-services/v3",
        v3Service: "",
        v3ClientSecret: "",
        v3CustomerId: "",
      });
    } catch (error) {
      console.error("Error loading parking config", error);
      message.error(t("parking.configLoadError", { defaultValue: "Could not load parking configuration." }));
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadBookings = async () => {
    if (!fleetId) return;
    setLoadingBookings(true);
    try {
      const response = await API_REQUEST_HEADERS_BASEURL.get(bookingsEndpoint);
      const data = pickResponseData(response) || {};
      setBookings(Array.isArray(data.items) ? sortBookingsNewestFirst(data.items) : []);
    } catch (error) {
      console.error("Error loading parking bookings", error);
      message.error(t("parking.bookingLoadError", { defaultValue: "Could not load parking bookings." }));
    } finally {
      setLoadingBookings(false);
    }
  };

  useEffect(() => {
    loadConfig();
    loadBookings();
  }, [fleetId]);

  const handleSaveConfig = async (values) => {
    if (!fleetId) return;
    setSavingConfig(true);
    try {
      const payload = {
        providerVersion: values.providerVersion || providerVersion,
        v2: {
          authKey: values.v2AuthKey || "",
        },
        v3: {
          authUrl: values.v3AuthUrl || "",
          realm: values.v3Realm || "",
          baseUrl: values.v3BaseUrl || "",
          service: values.v3Service || "",
          clientSecret: values.v3ClientSecret || "",
          customerId: values.v3CustomerId || "",
        },
      };
      const response = await API_REQUEST_HEADERS_BASEURL.put(configEndpoint, payload);
      const data = pickResponseData(response) || {};
      setConfig(data);
      setProviderVersion(data.providerVersion || payload.providerVersion || "v2");
      configForm.setFieldsValue({
        v2AuthKey: "",
        v3Service: "",
        v3ClientSecret: "",
        v3CustomerId: "",
      });
      message.success(t("parking.configSaved", { defaultValue: "Parking configuration saved." }));
    } catch (error) {
      console.error("Error saving parking config", error);
      message.error(fleetErrorMessage(t, error, t("parking.configSaveError")));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSearchAreas = async (values) => {
    if (!fleetId) return;
    setSearchingAreas(true);
    try {
      const response = await API_REQUEST_HEADERS_BASEURL.get(areasEndpoint, {
        params: {
          latitude: values.latitude,
          longitude: values.longitude,
          radiusMeters: values.radiusMeters || 50000,
          limit: values.limit || 10,
        },
      });
      const data = pickResponseData(response) || {};
      setParkingAreas(Array.isArray(data.items) ? data.items : []);
    } catch (error) {
      console.error("Error searching parking areas", error);
      message.error(fleetErrorMessage(t, error, t("parking.areaSearchError")));
    } finally {
      setSearchingAreas(false);
    }
  };

  const handleCreateBooking = async (values) => {
    if (!fleetId) return;
    setCreatingBooking(true);
    try {
      const payload = {
        parkingAreaId: values.parkingAreaId,
        plateNumber: values.plateNumber,
        startDate: toIsoString(values.startDate),
        endDate: toIsoString(values.endDate),
        firstName: values.firstName,
        lastName: values.lastName,
        comment: values.comment,
      };
      await API_REQUEST_HEADERS_BASEURL.post(bookingsEndpoint, payload);
      bookingForm.resetFields();
      message.success(t("parking.bookingCreated", { defaultValue: "Parking booking created." }));
      await loadBookings();
    } catch (error) {
      console.error("Error creating parking booking", error);
      message.error(fleetErrorMessage(t, error, t("parking.bookingCreateError")));
    } finally {
      setCreatingBooking(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!fleetId || !bookingId) return;
    try {
      const endpoint = replaceFleetId(API_Fleet_ID_parking_booking, fleetId).replace(
        "{bookingId}",
        encodeURIComponent(bookingId)
      );
      await API_REQUEST_HEADERS_BASEURL.delete(endpoint);
      message.success(t("parking.bookingCancelled", { defaultValue: "Parking booking cancelled." }));
      await loadBookings();
    } catch (error) {
      console.error("Error cancelling parking booking", error);
      message.error(fleetErrorMessage(t, error, t("parking.bookingCancelError")));
    }
  };

  const bookingColumns = [
    {
      title: t("parking.bookingId", { defaultValue: "Booking" }),
      dataIndex: "bookingId",
      key: "bookingId",
      render: (value) => <Text code>{value || "-"}</Text>,
    },
    {
      title: t("parking.parkingArea", { defaultValue: "Parking area" }),
      dataIndex: "parkingAreaId",
      key: "parkingAreaId",
      render: (value, record) => record.parkingAreaName || value || "-",
    },
    {
      title: t("parking.plate", { defaultValue: "Plate" }),
      dataIndex: "plateNumber",
      key: "plateNumber",
    },
    {
      title: t("parking.period", { defaultValue: "Period" }),
      key: "period",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{formatDate(record.startDate)}</Text>
          <Text type="secondary">{formatDate(record.endDate)}</Text>
        </Space>
      ),
    },
    {
      title: t("parking.status", { defaultValue: "Status" }),
      dataIndex: "status",
      key: "status",
      render: (value) => <Tag>{value || "UNKNOWN"}</Tag>,
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => (
        <Button danger size="small" disabled={!record.bookingId} onClick={() => handleCancelBooking(record.bookingId)}>
          {t("parking.cancelBooking", { defaultValue: "Cancel" })}
        </Button>
      ),
    },
  ];

  const areaColumns = [
    {
      title: t("parking.parkingArea", { defaultValue: "Parking area" }),
      dataIndex: "name",
      key: "name",
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <Text>{value || record.id}</Text>
          <Text code copyable>{record.id}</Text>
        </Space>
      ),
    },
    {
      title: t("parking.distance", { defaultValue: "Distance" }),
      dataIndex: "distanceKm",
      key: "distanceKm",
      render: (value) => (value == null ? "-" : `${value} km`),
    },
    {
      title: t("parking.spaces", { defaultValue: "Spaces" }),
      key: "spaces",
      render: (_, record) => {
        if (record.freeSpaces == null && record.totalSpaces == null) return "-";
        return `${record.freeSpaces ?? "?"}/${record.totalSpaces ?? "?"}`;
      },
    },
    {
      title: t("parking.address", { defaultValue: "Address" }),
      dataIndex: "address",
      key: "address",
      render: (value) => value || "-",
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>
            {t("parking.title", { defaultValue: "Parking" })}
          </Title>
          <Text type="secondary">
            {t("parking.subtitle", {
              defaultValue: "Configure Bosch parking credentials, create test bookings, and review booking history.",
            })}
          </Text>
        </div>

        {!fleetId ? (
          <Alert type="warning" message={t("parking.missingFleet", { defaultValue: "No fleet selected." })} />
        ) : null}

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={10}>
            <Card
              title={t("parking.providerConfig", { defaultValue: "Provider configuration" })}
              loading={loadingConfig}
            >
              {config ? (
                <Alert
                  type={config.configured ? "success" : "warning"}
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={
                    config.configured
                      ? t("parking.configured", { defaultValue: "Selected provider is configured." })
                      : t("parking.notConfigured", { defaultValue: "Selected provider is not fully configured." })
                  }
                  description={
                    providerVersion === "v3"
                      ? `${t("parking.v3", { defaultValue: "V3" })}: ${config?.v3?.servicePreview || "service?"} / ${config?.v3?.customerIdPreview || "customer?"}`
                      : `${t("parking.v2", { defaultValue: "V2" })}: ${config?.v2?.authKeyPreview || "global/default"}`
                  }
                />
              ) : null}

              <Form form={configForm} layout="vertical" onFinish={handleSaveConfig} initialValues={{ providerVersion }}>
                <Form.Item name="providerVersion" label={t("parking.version", { defaultValue: "Version" })}>
                  <Radio.Group onChange={(event) => setProviderVersion(event.target.value)}>
                    <Radio.Button value="v2">{t("parking.v2", { defaultValue: "V2" })}</Radio.Button>
                    <Radio.Button value="v3">{t("parking.v3", { defaultValue: "V3" })}</Radio.Button>
                  </Radio.Group>
                </Form.Item>

                {providerVersion === "v2" ? (
                  <Form.Item
                    name="v2AuthKey"
                    label={t("parking.v2AuthKey", { defaultValue: "V2 auth key" })}
                    extra={t("parking.secretHelp", { defaultValue: "Leave blank to keep the current secret." })}
                  >
                    <Input.Password autoComplete="new-password" />
                  </Form.Item>
                ) : (
                  <>
                    <Form.Item name="v3AuthUrl" label={t("parking.v3AuthUrl", { defaultValue: "V3 auth URL" })}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="v3Realm" label={t("parking.v3Realm", { defaultValue: "V3 realm" })}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="v3BaseUrl" label={t("parking.v3BaseUrl", { defaultValue: "V3 base URL" })}>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="v3Service"
                      label={t("parking.v3Service", { defaultValue: "V3 service / client ID" })}
                      extra={t("parking.secretHelp", { defaultValue: "Leave blank to keep the current secret." })}
                    >
                      <Input autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                      name="v3ClientSecret"
                      label={t("parking.v3Secret", { defaultValue: "V3 client secret" })}
                      extra={t("parking.secretHelp", { defaultValue: "Leave blank to keep the current secret." })}
                    >
                      <Input.Password autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item
                      name="v3CustomerId"
                      label={t("parking.v3CustomerId", { defaultValue: "V3 customer ID / scope" })}
                      extra={t("parking.secretHelp", { defaultValue: "Leave blank to keep the current value." })}
                    >
                      <Input autoComplete="off" />
                    </Form.Item>
                  </>
                )}

                <Button type="primary" htmlType="submit" loading={savingConfig}>
                  {t("parking.saveConfig", { defaultValue: "Save configuration" })}
                </Button>
              </Form>
            </Card>
          </Col>

          <Col xs={24} xl={14}>
            <Card title={t("parking.createBooking", { defaultValue: "Create booking" })}>
              <Form form={bookingForm} layout="vertical" onFinish={handleCreateBooking}>
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item name="parkingAreaId" label={t("parking.parkingAreaId", { defaultValue: "Parking area ID" })} rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="plateNumber" label={t("parking.plate", { defaultValue: "Plate" })} rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="startDate" label={t("parking.startDate", { defaultValue: "Start" })} rules={[{ required: true }]}>
                      <DatePicker showTime style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="endDate" label={t("parking.endDate", { defaultValue: "End" })} rules={[{ required: true }]}>
                      <DatePicker showTime style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="firstName" label={t("parking.firstName", { defaultValue: "First name" })} rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="lastName" label={t("parking.lastName", { defaultValue: "Last name" })} rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="comment" label={t("parking.comment", { defaultValue: "Comment" })}>
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </Col>
                </Row>
                <Button type="primary" htmlType="submit" loading={creatingBooking}>
                  {t("parking.createBooking", { defaultValue: "Create booking" })}
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>

        <Card title={t("parking.findParkingAreaId", { defaultValue: "Find parking area IDs" })}>
          <Form form={areaForm} layout="inline" onFinish={handleSearchAreas} initialValues={{ radiusMeters: 50000, limit: 10 }}>
            <Form.Item name="latitude" label={t("parking.latitude", { defaultValue: "Latitude" })} rules={[{ required: true }]}>
              <Input style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="longitude" label={t("parking.longitude", { defaultValue: "Longitude" })} rules={[{ required: true }]}>
              <Input style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="radiusMeters" label={t("parking.radiusMeters", { defaultValue: "Radius (m)" })}>
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="limit" label={t("parking.limit", { defaultValue: "Limit" })}>
              <Input style={{ width: 90 }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={searchingAreas}>
                {t("parking.search", { defaultValue: "Search" })}
              </Button>
            </Form.Item>
          </Form>
          <Table
            style={{ marginTop: 16 }}
            rowKey={(record) => record.id || record.parkingAreaId}
            dataSource={parkingAreas}
            columns={areaColumns}
            pagination={{ pageSize: 5 }}
          />
        </Card>

        <Card
          title={t("parking.bookings", { defaultValue: "Bookings" })}
          extra={
            <Button onClick={loadBookings} loading={loadingBookings}>
              {t("parking.refresh", { defaultValue: "Refresh" })}
            </Button>
          }
        >
          <Table
            rowKey={(record) => record.bookingId || `${record.parkingAreaId}-${record.startDate}`}
            dataSource={bookings}
            columns={bookingColumns}
            loading={loadingBookings}
            pagination={{ pageSize: 10 }}
          />
        </Card>
      </Space>
    </div>
  );
}
